import type { ReactNode } from 'react';
import { Button } from '@/components/ui/Button';
import { DEFAULT_CTA_BANNER_IMAGE, imageOrDefault } from '../default-images';
import { CtaLink } from '../CtaLink';
import type { SectionRendererProps } from '../section-renderer.types';

interface CtaBannerContent {
  eyebrow?: string;
  title: string;
  body?: string[];
  image?: string;
  ctaLabel: string;
  ctaUrl: string;
  note?: string;
  titlePrefix?: string;
  titleHighlight?: string;
  titleSuffix?: string;
}

/**
 * Sections rendered as a full-bleed dark band rather than the default light
 * background. Keyed by section `key`, not a content field - confirmed
 * against the live Figma file (docs/specs/02-design.md §17): the
 * ai-due-diligence and cta-banner (Benefits' Slim CTA) treatments came from
 * the client's 2026-09-19 design review; for-investors and dont-wait came
 * from checking the actual Figma frames afterward, since the docx never
 * covered them. `teal` (#00405C) is a different shade from `navy` (#001928)
 * - confirmed from Figma's own generated code, not assumed.
 */
const DARK_VARIANT: Partial<Record<string, 'green' | 'navy' | 'teal' | 'maroon' | 'mint'>> = {
  'ai-due-diligence': 'green',
  'cta-banner': 'navy',
  'dont-wait': 'teal',
  'for-investors': 'maroon',
  // Investors page legal-experience band - confirmed via Figma's own
  // get_design_context data: `--color-green` at 50% opacity (literally
  // `rgba(77,186,114,0.5)` in Figma) with dark text - see the `mint` entry
  // below. (Investors' own closing CTA band, "get-ready", was removed per
  // the client's changes doc - see 01-requirements.md A-119.)
  'backed-by-legal-experience': 'mint',
};

const VARIANT_CLASSES: Record<'default' | 'green' | 'navy' | 'teal' | 'maroon' | 'mint', string> = {
  default: 'bg-white text-text-primary',
  green: 'bg-dkgreen text-white',
  navy: 'bg-navy text-white',
  teal: 'bg-teal text-white',
  maroon: 'bg-maroon text-white',
  // `--color-green` at 50% opacity, dark text - confirmed exact value from
  // Figma (`rgba(77,186,114,0.5)`), not approximated.
  mint: 'bg-green/50 text-text-primary',
};

// Eyebrow color isn't uniform across dark variants, confirmed from Figma:
// ai-due-diligence's is white (matching the docx's "headers...white" note),
// for-investors' is yellow (the docx's explicit pink-to-yellow fix) - the
// existing yellow default below still covers that one. for-trusted-partners
// (a `default`/light-background section) is teal, not the generic dkgreen
// default fallback below.
const EYEBROW_CLASS: Partial<Record<string, string>> = {
  'ai-due-diligence': 'text-white',
  'for-trusted-partners': 'text-teal',
  // mint's dark text needs a dark eyebrow too - the yellow fallback below is
  // for the saturated dark bands, not this light one. Navy confirmed from
  // Figma's own "???????" placeholder eyebrow color, not guessed.
  'backed-by-legal-experience': 'text-navy',
};

// for-trusted-partners' title/body colors, confirmed from Figma, differ from
// the generic `default` fallback (navy title via ambient text-text-primary,
// gray body via text-text-secondary): dkgreen title, navy body.
// backed-by-legal-experience's title/body are both teal, confirmed from
// Figma's own get_design_context data, not dkgreen/text-primary.
const TITLE_CLASS: Partial<Record<string, string>> = {
  'for-trusted-partners': 'text-dkgreen',
  'backed-by-legal-experience': 'text-teal',
};
const BODY_CLASS: Partial<Record<string, string>> = {
  'for-trusted-partners': 'text-navy',
  'backed-by-legal-experience': 'text-teal',
};

// for-investors and for-trusted-partners' headings are 50px/60px line-height
// in Figma, noticeably larger than this component's shared text-h2 (32px) -
// confirmed from Figma's own generated code. A full replacement (font-weight
// and letter-spacing re-specified, not just size) rather than combining with
// text-h2 on the same element, since two font-size utilities on one element
// have an unpredictable override order in Tailwind's generated stylesheet.
const LARGE_TITLE_CLASS = 'text-[50px] leading-[60px] font-semibold tracking-[0.03125rem]';
const TITLE_SIZE_CLASS: Partial<Record<string, string>> = {
  'for-investors': LARGE_TITLE_CLASS,
  'for-trusted-partners': LARGE_TITLE_CLASS,
};

// ai-due-diligence's CTA is a dark navy button in Figma, not yellow like
// every other dark-variant cta-banner section. backed-by-legal-experience's
// mint background needs the standard filled dkgreen button (`primary`), not
// the yellow the dark-variant fallback below would otherwise give it.
const BUTTON_VARIANT_OVERRIDE: Partial<Record<string, 'navy' | 'primary'>> = {
  'ai-due-diligence': 'navy',
  'backed-by-legal-experience': 'primary',
};

// Layout is mirrored for this one section, confirmed from Figma's own DOM
// order (its image node comes before its text node, the reverse of
// ai-due-diligence and for-investors): image on the left, text on the right.
const REVERSE_LAYOUT = new Set(['for-trusted-partners']);

// for-investors and for-trusted-partners both size their image at 496x610px
// against an 866px text column in Figma - roughly 36% of the row, portrait-
// oriented - not an equal ~50/50 landscape split like every other section.
// An explicit max-width (rather than a percentage + flex-none) is what
// actually keeps it capped regardless of how much space `flex-1` on its
// text sibling would otherwise cede it.
const SMALL_PORTRAIT_IMAGE = new Set(['for-investors', 'for-trusted-partners']);
const DEFAULT_IMAGE_CLASS = 'aspect-4/3 w-full flex-1 rounded-2xl object-cover';
const SMALL_PORTRAIT_IMAGE_CLASS = 'aspect-[496/610] w-full max-w-96 rounded-2xl object-cover lg:shrink-0';

// dont-wait and cta-banner (Benefits' Slim CTA) have no image - Figma puts
// their title/body on the left and the CTA button as a separate sibling to
// the right, not stacked below the text like every other cta-banner section.
const BUTTON_BESIDE_TEXT = new Set(['dont-wait', 'cta-banner']);

interface ResolvedCtaBannerStyle {
  eyebrowClass: string;
  titleClass: string;
  titleSizeClass: string;
  bodyClass: string;
  buttonVariant: 'navy' | 'primary' | 'yellow';
  reversed: boolean;
  buttonBesideText: boolean;
  imageClass: string;
}

function resolveCtaBannerStyle(key: string, variant: 'default' | 'green' | 'navy' | 'teal' | 'maroon' | 'mint'): ResolvedCtaBannerStyle {
  return {
    eyebrowClass: EYEBROW_CLASS[key] ?? (variant === 'default' ? 'text-dkgreen' : 'text-yellow'),
    titleClass: TITLE_CLASS[key] ?? '',
    titleSizeClass: TITLE_SIZE_CLASS[key] ?? 'text-h2',
    bodyClass: BODY_CLASS[key] ?? (variant === 'default' ? 'text-text-secondary' : ''),
    buttonVariant: BUTTON_VARIANT_OVERRIDE[key] ?? (variant === 'default' ? 'primary' : 'yellow'),
    reversed: REVERSE_LAYOUT.has(key),
    buttonBesideText: BUTTON_BESIDE_TEXT.has(key),
    imageClass: SMALL_PORTRAIT_IMAGE.has(key) ? SMALL_PORTRAIT_IMAGE_CLASS : DEFAULT_IMAGE_CLASS,
  };
}

function CtaBannerCopy({
  c,
  style,
}: {
  c: CtaBannerContent;
  style: Pick<ResolvedCtaBannerStyle, 'eyebrowClass' | 'titleClass' | 'titleSizeClass' | 'bodyClass'>;
}) {
  const hasHighlightParts = Boolean(c.titlePrefix || c.titleHighlight || c.titleSuffix);
  return (
    <>
      {c.eyebrow && <p className={`text-p6 font-semibold uppercase ${style.eyebrowClass}`}>{c.eyebrow}</p>}
      {hasHighlightParts ? (
        <h2 className={`${style.titleSizeClass} ${style.titleClass}`}>
          {c.titlePrefix}
          <span className="text-yellow">{c.titleHighlight}</span>
          {c.titleSuffix}
        </h2>
      ) : (
        <h2 className={`${style.titleSizeClass} ${style.titleClass}`}>{c.title}</h2>
      )}
      {c.body?.map((paragraph, i) => (
        <p key={i} className={`text-p4 ${style.bodyClass}`}>
          {paragraph}
        </p>
      ))}
      {c.note && <p className={`text-p6 ${style.bodyClass}`}>{c.note}</p>}
    </>
  );
}

/**
 * dont-wait/cta-banner (no image, button beside text): title/body in their
 * own column, button as a separate sibling to the right - confirmed from
 * Figma. Every other section: eyebrow/title/body/button stacked in one
 * column, next to the image, ordered per `reversed` (image-then-text or
 * text-then-image).
 */
function renderCtaBannerContent(
  style: Pick<ResolvedCtaBannerStyle, 'buttonBesideText' | 'reversed'>,
  copy: ReactNode,
  ctaButton: ReactNode,
  imageEl: ReactNode,
): ReactNode {
  if (style.buttonBesideText) {
    return (
      <>
        {/* max-w-2xl (not a bare flex-1) keeps this from stretching edge-to-edge
          toward the button - Figma's text column is narrower than the full
          row, with real empty space before the button, not a flush edge. */}
        <div className="flex flex-1 flex-col items-center gap-4 lg:max-w-2xl lg:items-start">{copy}</div>
        {ctaButton}
      </>
    );
  }

  const textBlock = (
    <div className="flex flex-1 flex-col items-center gap-4 lg:items-start">
      {copy}
      {ctaButton}
    </div>
  );
  return style.reversed ? (
    <>
      {imageEl}
      {textBlock}
    </>
  ) : (
    <>
      {textBlock}
      {imageEl}
    </>
  );
}

/**
 * CTA banner section (FR-111, FR-113, FR-116, FR-117, FR-122, FR-126) - the
 * most reused type: Home's Premium band, Investors band, Trusted Partners
 * band, and both pages' Closing CTA band, plus Benefits' Slim CTA band.
 */
export function CtaBannerRenderer({ section }: SectionRendererProps) {
  const c = section.content as unknown as CtaBannerContent;
  const variant = DARK_VARIANT[section.key] ?? 'default';
  const style = resolveCtaBannerStyle(section.key, variant);

  const copy = <CtaBannerCopy c={c} style={style} />;
  const ctaButton = (
    <Button asChild variant={style.buttonVariant} className="w-fit shrink-0">
      <CtaLink to={c.ctaUrl}>{c.ctaLabel}</CtaLink>
    </Button>
  );
  // Falls back to this section's default image when the CMS has none set - see default-images.ts.
  const image = imageOrDefault(c.image, DEFAULT_CTA_BANNER_IMAGE[section.key]);
  const imageEl = image && <img src={image} alt="" className={style.imageClass} />;
  const content = renderCtaBannerContent(style, copy, ctaButton, imageEl);

  return (
    <section className={`w-full ${VARIANT_CLASSES[variant]}`}>
      <div className="mx-auto flex w-full max-w-328 flex-col items-center gap-6 px-4 py-16 text-center lg:flex-row lg:justify-between lg:text-left">
        {content}
      </div>
    </section>
  );
}
